// Injection seam for the shared transaction-form stack.
//
// FormRenderer / LookupModal are identical across the designer (live
// preview + deployed-site embeds) and the standalone runtime. The only
// thing that differs is the *transport* — which HTTP endpoints back the
// lookup picker, the "add new master" save, and document upload — plus how
// notifications are shown. Each host app fills a `TxnRuntime` and wraps the
// form tree in `<TxnRuntimeProvider>`; the shared components read it via
// `useTxnRuntime()`. The package therefore carries no axios / toast / app
// imports of its own.

import { createContext, useContext, useMemo } from 'react'
import type { ReactNode } from 'react'
import type {
  DeployResponse, LookupResponse, SaveResponse, RunActionResponse,
} from './contracts'
import type {
  AssistantActionRequest, ImportActionRequest, OpenActionRequest, PaymentActionRequest,
  SendActionRequest, WizardActionRequest,
} from './types'
import type { ReportRuntimeAdapter } from '../report/types'
import { instrumentTxnRuntime } from '../activity'

// Request for a custom action button (engine action ``run_action``). The host
// posts the current form values; the engine runs the action's pre-action
// validations and (on pass) its handler, returning a RunActionResponse.
export interface RunActionRequest {
  formNo: string
  actionName: string
  header: Record<string, unknown>
  headerValues?: Record<string, unknown>
  txnId?: string
  parentPk?: Record<string, unknown>
  // Warning codes the user acknowledged via "ignore warnings and proceed" —
  // a retry after a 422 VALIDATION_WARNINGS passes the codes back so the
  // engine (and the business logic itself) skips those warnings.
  ignoreWarnings?: string[]
  // Confirmation answer for an action whose first call returned
  // handler='confirm'. Re-call with this set to clear the gate and run the
  // effect: ``acknowledged`` for a yes/no question, ``answer`` for the typed
  // phrase / one-time code / password, or ``resend`` to email a fresh code.
  confirm?: { acknowledged?: boolean; answer?: string; resend?: boolean }
  // A button on a LINE editor: whether the line is a new one (Vision's
  // updateFlag 'A') and how many lines the form holds — DespatchAct's Default
  // reads both. Absent everywhere else.
  rowIsNew?: boolean
  lineCount?: number
  // A Vision pick-list button's second call (SERVICE_HANDLER 4): the rows the
  // user ticked in its popup, exactly as listed — the engine runs the
  // button's transform over them and answers with the lines.
  selectedRows?: Array<Record<string, unknown>>
}

// Request for a BULK custom action (engine action ``run_action_bulk``): run
// one action against many selected records. The confirmation gate (if any) is
// cleared ONCE for the whole batch — the same ``confirm`` loop as a single
// action — then the engine runs the action's eligibility + business logic per
// record and returns a handler='bulk_result' response.
export interface RunBulkActionRequest {
  formNo: string
  actionName: string
  // Selected records' transaction ids (ListView/EmbedView ``_txn_id``).
  txnIds: string[]
  ignoreWarnings?: string[]
  confirm?: { acknowledged?: boolean; answer?: string; resend?: boolean }
}

export interface UploadResult {
  url: string
  name?: string
}

export interface TxnNotifier {
  success: (msg: string) => void
  error: (msg: string) => void
  // Informational toast (severity-'P' prompts). Optional — callers fall back
  // to `success` when the host doesn't provide it.
  info?: (msg: string) => void
  // Warning toast (non-blocking business-logic warnings). Optional — callers
  // fall back to `info`, then `success`, when the host doesn't provide it.
  warn?: (msg: string) => void
}

export interface TxnRuntime {
  // Lookup picker data + the master-record "add" flow (signatures mirror the
  // designer livePreview* functions so call sites move verbatim).
  lookup: (
    project: string,
    objName: string,
    lookup: string,
    search?: string,
    limit?: number,
    opts?: {
      filter?: string
      formValues?: Record<string, unknown>
      headerValues?: Record<string, unknown>
      literalValues?: Record<string, unknown>
      lookupSql?: string
      lookupSqlDataCol?: string
      // The field the picker fills: an imported Vision pophelp's ``?`` marks
      // read SQL_INPUT refs, and this tells its own mark from the others.
      lookupField?: string
      // The field a reused (inherited) Vision pophelp was written for.
      lookupInheritedField?: string
    },
  ) => Promise<LookupResponse>
  lookupSource: (
    project: string,
    opts: { table?: string; objName?: string },
  ) => Promise<{ ok: true; obj_name: string; transaction: unknown }>
  resolveDefaults: (
    project: string,
    objName: string,
  ) => Promise<{ ok: boolean; values: Record<string, unknown>; protect?: Record<string, 0 | 1> }>
  save: (
    project: string,
    objName: string,
    header: Record<string, unknown>,
    txnId?: string,
    opts?: { ignoreWarnings?: string[] },
  ) => Promise<SaveResponse>
  // Designer deploys the freshly-authored master object before saving into
  // it; the standalone runtime is already deployed, so it supplies a no-op.
  deploy: (project: string, objName: string) => Promise<DeployResponse>
  // Document/image upload widget transport. Returns the served URL to store.
  uploadDocument: (file: File) => Promise<UploadResult>
  // Image placed INSIDE a RICHTEXT column's body. Returns a PUBLIC URL (the
  // same body renders on published pages, where document-store URLs are
  // refused). Optional — without it the editor offers insert-by-URL only.
  uploadContentImage?: (project: string, objName: string, file: File) => Promise<UploadResult>
  // Run a custom action button. The engine validates first; a failed
  // validation rejects with the standard ValidationFailed error envelope (the
  // host's error parser surfaces the messages). On success it resolves with a
  // RunActionResponse describing what the handler produced.
  runAction: (
    project: string,
    objName: string,
    req: RunActionRequest,
  ) => Promise<RunActionResponse>
  // Run a custom action against MANY selected records in one call. Optional —
  // hosts that surface list multi-select wire it; the confirmation gate (if
  // any) clears once for the batch and the response is a handler='bulk_result'
  // (or handler='confirm' on the first, un-answered call).
  runBulkAction?: (
    project: string,
    objName: string,
    req: RunBulkActionRequest,
  ) => Promise<RunActionResponse>
  // Build a Report (obj_type "R") viewer adapter for handler='report' action
  // buttons. Optional — hosts that can render Jasper reports supply it
  // (platform: /api/report-data; standalone runtime: /app/<p>/api/report-data);
  // when absent a report action surfaces a "not available here" error.
  makeReportAdapter?: (reportObjName: string) => ReportRuntimeAdapter
  // Open the host's Assistant surface for a handler='assistant' action button
  // (a deterministic intent-result modal, or the chat seeded with a prompt).
  // Optional — when absent the button surfaces a "not available here" error.
  openAssistantAction?: (req: AssistantActionRequest) => void
  // Open the host's checkout for a handler='payment' action button. The host
  // owns the gateway SDK — the platform's Run view, the published site and a
  // downloaded runtime each load it differently — so this layer only says
  // WHICH record is being paid for and under which binding. Optional; when
  // absent the button surfaces a "not available here" error rather than
  // pretending to charge.
  openPaymentAction?: (req: PaymentActionRequest) => void
  // Open the host's file-import window for a handler='import' action button:
  // pick a file, map its columns, review it side by side and save. Optional;
  // when absent the button surfaces a "not available here" error.
  openImportAction?: (req: ImportActionRequest) => void
  // Open ANOTHER object for a handler='open' action button — on its own
  // screen ('navigate') or in a slide-over above this one ('drawer'). Only
  // the host owns navigation and knows how to draw a process / dashboard /
  // visual, so the screen just says WHAT to open and with which values (a
  // Report never reaches here: it opens in the screen's own report window).
  // Optional; when absent the button surfaces a "not available here" error.
  openObjectAction?: (req: OpenActionRequest) => void
  // Start a Transaction Wizard for a handler='wizard' action button — on its
  // own screen ('navigate') or in a slide-over ('drawer'). The host mounts the
  // shared WizardRunner; this layer only says WHICH wizard and its launch
  // inputs. Optional; when absent the button surfaces a "not available here"
  // error.
  openWizardAction?: (req: WizardActionRequest) => void
  // Open the host's SEND window for a handler='send' action button (the
  // preview with the default template, the composer, the progress list) over
  // the given records. Optional; when absent the button surfaces a "not
  // available here" error.
  openSendAction?: (req: SendActionRequest) => void
  // Open the message log of ONE record (its Messages) — what the row ⋮ /
  // form header "Messages" item opens when the object has a send. Optional.
  openMessageLog?: (objName: string, txnId: string) => void
  notify: TxnNotifier
}

const TxnRuntimeContext = createContext<TxnRuntime | null>(null)

export function TxnRuntimeProvider(
  { value, children }: { value: TxnRuntime; children: ReactNode },
) {
  // Saves / actions / uploads through the shared form stack are recorded for
  // activity analytics — once, here, for every host (idempotent wrapper; a
  // no-op until a host has installed the tracker).
  const instrumented = useMemo(() => instrumentTxnRuntime(value), [value])
  return (
    <TxnRuntimeContext.Provider value={instrumented}>
      {children}
    </TxnRuntimeContext.Provider>
  )
}

// The same transport, for a component that can do something USEFUL without
// it — the enforced-filter bar renders its inputs either way and only needs
// this for a condition's reference picker. Returns null instead of throwing
// so such a component never forces a host to wire transport it doesn't use.
export function useTxnRuntimeOptional(): TxnRuntime | null {
  return useContext(TxnRuntimeContext)
}

export function useTxnRuntime(): TxnRuntime {
  const ctx = useContext(TxnRuntimeContext)
  if (!ctx) {
    throw new Error(
      'useTxnRuntime must be used within a <TxnRuntimeProvider>. The shared '
      + 'transaction form (FormRenderer / LookupModal) needs a host-provided '
      + 'transport.',
    )
  }
  return ctx
}
